class WellnessRowAnimation extends HTMLElement {
  constructor() {
    super();

    this.ROTATE_DEG = ['0deg', '120deg', '240deg'];
    this.sectionInstance = null;
    this.sectionId = this.dataset.shopifySectionId || this.getAttribute('data-shopify-section-id');

    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleSectionUnload = this.handleSectionUnload.bind(this);
    this.handleDOMContentLoaded = this.handleDOMContentLoaded.bind(this);
  }

  connectedCallback() {
    if (this._isConnected) {
      return;
    }

    this._isConnected = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.handleDOMContentLoaded, { once: true });
    } else {
      this.initSection();
    }

    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('shopify:section:unload', this.handleSectionUnload);
  }

  disconnectedCallback() {
    if (!this._isConnected) {
      return;
    }

    this._isConnected = false;

    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
    document.removeEventListener('DOMContentLoaded', this.handleDOMContentLoaded);

    this.destroySection();
  }

  handleDOMContentLoaded() {
    this.initSection();
  }

  initSection() {
    const section = this.querySelector('.section-wellness-cta');

    if (!section) {
      this.destroySection();
      return;
    }

    this.sectionElement = section;

    this.destroySection();
    this.sectionInstance = this.setupSection(section);
  }

  handleSectionLoad(event) {
    if (!event?.detail || !event.detail.sectionId) {
      return;
    }

    if (!this.sectionId) {
      this.sectionId = this.dataset.shopifySectionId || this.getAttribute('data-shopify-section-id');
    }

    if (event.detail.sectionId !== this.sectionId) {
      return;
    }

    requestAnimationFrame(() => {
      this.initSection();
    });
  }

  handleSectionUnload(event) {
    if (!event?.detail || !event.detail.sectionId) {
      return;
    }

    if (!this.sectionId) {
      this.sectionId = this.dataset.shopifySectionId || this.getAttribute('data-shopify-section-id');
    }

    if (event.detail.sectionId !== this.sectionId) {
      return;
    }

    this.destroySection();
  }

  setupSection(section) {
    const imageHolder = section.querySelector('[data-image-holder]');
    const image = section.querySelector('[data-rotate-image]');

    if (!imageHolder || !image) {
      return {
        destroy() {}
      };
    }

    const headerAttr = parseInt(section.dataset.headerSize, 10);
    const headerSize = Number.isNaN(headerAttr) ? 70 : headerAttr;
    const desktopVideo = section.querySelector('[data-video-desktop]');
    const mobileVideo = section.querySelector('[data-video-mobile]');
    const mediaQuery = window.matchMedia('(max-width: 767px)');

    let rafId = null;
    const observers = [];

    const computeRotation = () => {
      const holderRect = imageHolder.getBoundingClientRect();
      const isMobile = mediaQuery.matches;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const { height, top } = holderRect;
      const offsetTop = window.scrollY + top;
      const reduceViewport = isMobile ? -headerSize : 0;
      const scrolled = viewportHeight - top - reduceViewport;

      if (
        window.scrollY + viewportHeight - height < offsetTop - height ||
        top < 0 ||
        scrolled < 0
      ) {
        return;
      }

      let index;

      if (isMobile) {
        const viewportSlice = (viewportHeight - headerSize) / 3;
        if (viewportSlice <= 0) {
          index = 0;
        } else {
          index = Math.floor((scrolled - headerSize) / viewportSlice);
        }
      } else {
        const slice = viewportHeight / 3;
        index = slice <= 0 ? 0 : Math.floor(scrolled / slice);
      }

      if (!Number.isFinite(index)) {
        index = 0;
      }

      index = Math.max(0, Math.min(index, this.ROTATE_DEG.length - 1));
      image.style.setProperty('transform', `rotate(${this.ROTATE_DEG[index]})`);
    };

    const onScroll = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        computeRotation();
      });
    };

    const observeVideo = (video) => {
      if (!video || typeof IntersectionObserver === 'undefined') {
        return null;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const maybePlay = video.play();
            if (maybePlay && typeof maybePlay.catch === 'function') {
              maybePlay.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.5 });

      observer.observe(video);
      return observer;
    };

    const handleMediaChange = () => {
      computeRotation();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('load', computeRotation);
    computeRotation();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }

    const desktopObserver = observeVideo(desktopVideo);
    const mobileObserver = observeVideo(mobileVideo);

    if (desktopObserver) {
      observers.push(desktopObserver);
    }

    if (mobileObserver) {
      observers.push(mobileObserver);
    }

    return {
      destroy() {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('load', computeRotation);

        if (typeof mediaQuery.removeEventListener === 'function') {
          mediaQuery.removeEventListener('change', handleMediaChange);
        } else if (typeof mediaQuery.removeListener === 'function') {
          mediaQuery.removeListener(handleMediaChange);
        }

        observers.forEach((observer) => observer.disconnect());

        if (rafId) {
          window.cancelAnimationFrame(rafId);
        }
      }
    };
  }

  destroySection() {
    if (this.sectionInstance && typeof this.sectionInstance.destroy === 'function') {
      this.sectionInstance.destroy();
    }

    this.sectionInstance = null;
  }
}

customElements.define('wellness-row-animation', WellnessRowAnimation);
